import React, { useState, useEffect, useRef } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import DOMPurify from 'dompurify';
import { useTenant } from '../context/TenantContext';
import { useAdminAccess } from '../hooks/useAdminAccess';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.archiveabyss.com';

interface Conversation {
  id: string;
  name: string;
  lastMessage: string;
  timestamp: string;
  lastSender: string;
}

interface Message {
  id: string;
  sender: string;
  content: string;
  timestamp: string;
  attachments?: FileAttachment[];
}

interface FileAttachment {
  name: string;
  url: string;
  size?: string;
  type?: string;
}

interface Props {
  user: any;
  signOut: () => void;
}

const TeamsArchive: React.FC<Props> = ({ user, signOut }) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [activeConv, setActiveConv] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(false);
  const [msgCursor, setMsgCursor] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesRef = useRef<HTMLDivElement>(null);

  const { config } = useTenant();
  const { isAdmin } = useAdminAccess();
  const branding = config?.branding;

  // Get display name from user
  const getUserDisplayName = () => {
    const email = user?.signInDetails?.loginId || user?.username || '';
    if (email.includes('@')) {
      const local = email.split('@')[0];
      return local
        .replace(/[._-]+/g, ' ')
        .split(/\s+/)
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    return email;
  };

  useEffect(() => {
    loadInbox();
  }, []);

  const getAuthHeader = async () => {
    const session = await fetchAuthSession();
    return `Bearer ${session.tokens?.idToken?.toString()}`;
  };

  const loadInbox = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/inbox?limit=100`, {
        headers: { 'Authorization': await getAuthHeader() }
      });
      const data = await response.json();
      setConversations(data.conversations || []);
    } catch (error) {
      console.error('Failed to load inbox:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (conv: Conversation, loadMore = false) => {
    try {
      setLoading(true);
      const cursor = loadMore ? msgCursor : null;
      const url = `${API_URL}/conversations/${encodeURIComponent(conv.id)}?limit=50${cursor ? `&cursor=${cursor}` : ''}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': await getAuthHeader() }
      });
      const data = await response.json();
      
      const newMessages = data.messages || [];
      
      if (loadMore) {
        // Add older messages to the top, maintain chronological order
        const sortedNew = newMessages.sort((a: Message, b: Message) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        setMessages(prev => [...sortedNew, ...prev]);
      } else {
        // New conversation - sort messages by timestamp and scroll to bottom
        const sortedMessages = newMessages.sort((a: Message, b: Message) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        setMessages(sortedMessages);
        setActiveConv(conv);
        setTimeout(() => {
          if (messagesRef.current) {
            messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
          }
        }, 100);
      }
      
      setMsgCursor(data.nextCursor || null);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const isMyMessage = (sender: string) => {
    const userEmail = user?.signInDetails?.loginId || user?.username || '';
    return sender.toLowerCase().includes(userEmail.toLowerCase().split('@')[0]);
  };

  const cleanConversationName = (name: string) => {
    if (!name) return 'Conversation';
    
    // If it's an email, extract the local part and clean it
    if (name.includes('@')) {
      const local = name.split('@')[0];
      return local
        .replace(/[._-]+/g, ' ')
        .trim()
        .split(/\s+/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    
    // If it's multiple emails separated by commas
    if (name.includes(',')) {
      return name
        .split(',')
        .map(email => {
          const clean = email.trim();
          if (clean.includes('@')) {
            const local = clean.split('@')[0];
            return local
              .replace(/[._-]+/g, ' ')
              .trim()
              .split(/\s+/)
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
          }
          return clean;
        })
        .slice(0, 2) // Only show first 2 names
        .join(', ');
    }
    
    return name;
  };

  const filteredMessages = messages.filter(msg => 
    searchQuery === '' || 
    msg.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
    msg.sender.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Removed file processing logic

  const getInitials = (name: string) => {
    if (!name) return '?';
    const parts = name.split(/[<@,; ]+/).filter(Boolean);
    const first = parts[0] || '?';
    const second = parts[1] || '';
    return (first[0] || '?').toUpperCase() + (second[0] ? second[0].toUpperCase() : '');
  };

  const handleScroll = () => {
    const element = messagesRef.current;
    if (!element || loading || !msgCursor || !activeConv) return;
    
    // Check if scrolled to top
    if (element.scrollTop < 100) {
      const prevScrollHeight = element.scrollHeight;
      loadMessages(activeConv, true).then(() => {
        // Maintain scroll position after loading older messages
        setTimeout(() => {
          if (messagesRef.current) {
            const newScrollHeight = messagesRef.current.scrollHeight;
            messagesRef.current.scrollTop = newScrollHeight - prevScrollHeight;
          }
        }, 50);
      });
    }
  };

  const formatTime = (timestamp: string) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const sameDay = date.toDateString() === now.toDateString();
      return sameDay
        ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : date.toLocaleDateString([], { year: '2-digit', month: 'short', day: 'numeric' });
    } catch {
      return '';
    }
  };

  return (
    <div className="teams-archive">
      <div className="sidebar">
        <div className="header">
          <div className="header-brand">
            {branding?.logo_url ? (
              <img src={branding.logo_url} alt={branding.company_name} className="header-logo" />
            ) : (
              <h1>{branding?.company_name || 'ArchiveAbyss'}</h1>
            )}
          </div>
          <div className="header-user">
            <span className="user-name">{getUserDisplayName()}</span>
            <div className="header-actions">
              {isAdmin && <a href="/admin" className="admin-link">Admin</a>}
              <button onClick={signOut}>Sign Out</button>
            </div>
          </div>
        </div>
        <div className="conversations">
          {loading && conversations.length === 0 && (
            <div className="loading">Loading conversations...</div>
          )}
          {conversations.map(conv => (
            <div 
              key={conv.id}
              className={`conversation ${activeConv?.id === conv.id ? 'active' : ''}`}
              onClick={() => loadMessages(conv)}
            >
              <div className="avatar">{getInitials(cleanConversationName(conv.name))}</div>
              <div className="conv-main">
                <div className="conv-header">
                  <div className="conv-name">{cleanConversationName(conv.name)}</div>
                  <div className="conv-time">{formatTime(conv.timestamp)}</div>
                </div>
                <div className="conv-preview">
                  {conv.lastSender ? `${conv.lastSender}: ` : ''}{conv.lastMessage}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <div className="main">
        <div className="main-header">
          <div>
            <h2>{activeConv ? cleanConversationName(activeConv.name) : 'Select a conversation'}</h2>
          </div>
          {activeConv && (
            <input 
              className="search-input"
              placeholder="Search messages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          )}
        </div>
        <div className="messages" ref={messagesRef} onScroll={handleScroll}>
          {!activeConv && (
            <div className="empty-state">Choose a conversation to view messages</div>
          )}
          {loading && activeConv && (
            <div className="loading">Loading messages...</div>
          )}
          
          {filteredMessages.map(msg => {
            const isMine = isMyMessage(msg.sender);
            return (
              <div key={msg.id} className={`message ${isMine ? 'mine' : 'theirs'}`}>
                {!isMine && <div className="avatar">{getInitials(msg.sender)}</div>}
                <div className="message-bubble">
                  <div className="message-meta">
                    <div className="sender">{msg.sender}</div>
                    <div className="time">{new Date(msg.timestamp).toLocaleString()}</div>
                  </div>
                  <div 
                    className="content" 
                    dangerouslySetInnerHTML={{ 
                      __html: DOMPurify.sanitize(msg.content || '', { USE_PROFILES: { html: true } })
                    }} 
                  />
                </div>
                {isMine && <div className="avatar">{getInitials(msg.sender)}</div>}
              </div>
            );
          })}

        </div>
      </div>
    </div>
  );
};

export default TeamsArchive;